/**
 * © 2024 Little Shilling, Inc.
 * Shon Little
 * Created: 2024-07-24
 */

// Add third-party dependencies.
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Box, Typography, Button, IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

// Add local dependencies.
import { useResetPasswordConfirmMutation } from '../../api/authSlice';
import ErrorAlert from '../Common/ErrorAlert';
import PageSpinner from '../Common/PageSpinner';
import FormText from '../Common/FormText';

/**
 * ResetPasswordConfirm component.
 * @example
 * return (
 *   <ResetPasswordConfirm />
 * )
 * @returns {React.ReactElement} component.
 */
const ResetPasswordConfirm = () => {
  // Set state hooks.
  const { uid, token } = useParams();
  const [error, setError] = useState(null);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Set mutation hooks.
  const [resetPasswordConfirm, { isLoading, error: resetError }] = useResetPasswordConfirmMutation();

  // Set navigate hook.
  const navigate = useNavigate();

  // Initialize react-hook-form.
  const { handleSubmit, control, getValues } = useForm();

  // Set error message.
  useEffect(() => {
    if (resetError) {
      console.error('Error: ', resetError);
      setError('Failed to reset password. Please try again.');
    }
  }, [resetError]);

  // Render loading indicator while checking the auth state.
  if (isLoading) return <PageSpinner />;

  /**
   * Handle form submission.
   * @param {Object} data - Form data.
   * @returns {void}
   */
  const onSubmit = async (data) => {
    if (data.newPassword !== data.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    try {
      await resetPasswordConfirm({ uid, token, newPassword: data.newPassword }).unwrap();
      navigate('/login');
    } catch (err) {
      setError('Failed to reset password. Please try again.');
      console.error('Error: ', err);
    }
  };

  /**
   * Handle key press events.
   * @param {Event} event - Key press event.
   * @returns {void}
   */
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmit(onSubmit)();
    }
  };

  // Password validation rules
  const passwordValidationRules = {
    required: 'New Password is required',
    minLength: {
      value: 8,
      message: 'Password must be at least 8 characters long',
    },
    pattern: {
      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/,
      message: 'Password must contain at least one uppercase letter, one lowercase letter, and one number',
    },
  };

  // Render component.
  return (
    <Box p={3}>
      <Typography variant="h1">Reset Password</Typography>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ mt: 1, maxWidth: 400 }}>
        {error && <ErrorAlert error={error} fallback="An error occurred. Please try again." />}
        <FormText
          control={control}
          name="newPassword"
          label="New Password"
          type={showNewPassword ? 'text' : 'password'}
          autoFocus
          rules={passwordValidationRules}
          showPasswordRequirements
          inputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle new password visibility"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  onMouseDown={(event) => event.preventDefault()}
                  edge="end"
                >
                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <FormText
          control={control}
          name="confirmPassword"
          label="Confirm Password"
          type={showConfirmPassword ? 'text' : 'password'}
          onKeyDown={handleKeyDown}
          rules={{
            required: 'Confirm Password is required',
            validate: (value) => value === getValues('newPassword') || 'Passwords do not match',
          }}
          inputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle confirm password visibility"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  onMouseDown={(event) => event.preventDefault()}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
          Reset Password
        </Button>
      </Box>
    </Box>
  );
};

// Export component.
export default ResetPasswordConfirm;
